import Layout from "./Layout";

export default function RefundPolicy() {
  return (
    <Layout>
      <div className="bg-white text-black">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 py-12 lg:py-16">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-wide">
              Refund & Damage Policy
            </h1>

            <div className="w-16 h-1 bg-hover_dark_primary mt-4 mb-6"></div>

            <p className="text-sm text-gray-600">
              <span className="font-semibold text-black">Effective Date:</span>{" "}
              August 31, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="space-y-5 text-[15px] sm:text-base leading-7 text-gray-700">
            <p>
              At <strong className="text-black">Legacy Decor</strong>, we take
              great care in packing and dispatching every home décor and gifting
              product so that it reaches you safely and in perfect condition.
            </p>

            <p>
              As our products are carefully selected, packed and dispatched
              specifically against customer orders,{" "}
              <strong className="text-black">
                we do not accept returns, exchanges or refunds for reasons other
                than damage caused during transportation
              </strong>
              , subject to the conditions mentioned below.
            </p>
          </div>

          {/* Policy Sections */}
          <div className="mt-10 space-y-9 text-[15px] sm:text-base leading-7 text-gray-700">
            {/* 1 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                1. No Returns or Refunds for Change of Mind
              </h2>

              <p>
                Once an order has been successfully placed and dispatched, we do
                not accept returns, exchanges or refunds for reasons including,
                but not limited to:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Change of mind or change in personal preference</li>
                <li>Product no longer required</li>
                <li>Product not matching personal taste or décor</li>
                <li>
                  Incorrect selection of colour, design, size or variant by the
                  customer
                </li>
                <li>Product ordered by mistake</li>
                <li>
                  Product being unsuitable for the intended location or purpose
                </li>
                <li>Delay in deciding whether to keep the product</li>
                <li>
                  Minor variations in colour, texture, finish, grain or
                  appearance that are inherent to the product or may occur due
                  to photography, lighting or screen settings
                </li>
                <li>
                  Any other reason unrelated to damage caused during
                  transportation
                </li>
              </ul>

              <p className="mt-4">
                <strong className="text-black">
                  All purchases are considered final once the order has been
                  dispatched
                </strong>
                , except where a valid transportation-damage claim is
                established or where otherwise required under applicable law.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                2. Damage During Transportation
              </h2>

              <p>
                If you receive a product that has been damaged during
                transportation, you must notify Legacy Decor{" "}
                <strong className="text-black">
                  within 24 hours of delivery
                </strong>
                .
              </p>

              <p className="mt-3">
                To enable us to verify the damage and process your claim, you
                must provide:
              </p>

              <ol className="list-decimal pl-6 mt-3 space-y-1">
                <li>Clear photographs of the damaged product</li>
                <li>
                  Clear photographs of the outer packaging from all relevant
                  sides
                </li>
                <li>
                  Photographs showing the shipping label/AWB on the package
                </li>
                <li>A short description of the damage</li>
              </ol>

              <p className="mt-4">
                Where possible, customers are requested to{" "}
                <strong className="text-black">
                  record an unboxing video from the moment the sealed package is
                  opened
                </strong>
                , particularly for fragile or high-value products.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                3. Important: Do Not Discard the Packaging
              </h2>

              <p>For any transportation-damage claim, please retain:</p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>The original product</li>
                <li>All original packaging material</li>
                <li>Box/carton</li>
                <li>Protective material</li>
                <li>Labels and shipping stickers</li>
                <li>Any other material received with the shipment</li>
              </ul>

              <p className="mt-4">
                We may require photographs, videos or inspection of the
                packaging and product to determine whether the damage occurred
                during transportation.
              </p>

              <p className="mt-3">
                Failure to retain the original packaging may affect our ability
                to process a transportation-damage claim.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                4. Assessment of Damage Claims
              </h2>

              <p>
                All damage claims will be reviewed by Legacy Decor based on the
                information and photographs/videos provided by the customer.
              </p>

              <p className="mt-3">
                A claim will be considered only where the damage is reasonably
                attributable to transportation or handling during delivery.
              </p>

              <p className="mt-3">
                The following will generally{" "}
                <strong className="text-black">not qualify</strong> as
                transportation damage:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Damage caused after delivery</li>
                <li>
                  Damage caused during installation, assembly or handling by the
                  customer
                </li>
                <li>Scratches, breakage or other damage caused by misuse</li>
                <li>Damage caused by improper cleaning or maintenance</li>
                <li>Normal wear and tear</li>
                <li>
                  Minor manufacturing or finishing variations that are inherent
                  to the product
                </li>
                <li>
                  Damage reported after the stipulated reporting period without
                  sufficient evidence
                </li>
                <li>
                  Any alteration or repair carried out by the customer or a
                  third party
                </li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                5. Resolution of an Approved Damage Claim
              </h2>

              <p>
                If Legacy Decor determines that a product was damaged during
                transportation, we may, at our discretion and subject to product
                availability:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Provide a replacement of the damaged product</li>
                <li>Provide a refund for the affected product</li>
              </ul>

              <p className="mt-4">
                The method of resolution will depend on the nature and extent of
                the damage, product availability and other relevant
                circumstances.
              </p>

              <p className="mt-3">
                Where a replacement is approved, Legacy Decor will arrange the
                replacement process and provide further instructions to the
                customer.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                6. Refund Processing
              </h2>

              <p>
                Where a refund is approved, the refund will generally be
                processed to the original payment method used for the purchase.
              </p>

              <p className="mt-3">
                The time taken for the amount to reflect in the customer's
                account may vary depending on the payment gateway, bank or
                financial institution.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                7. Delivery Inspection
              </h2>

              <p>
                Customers are encouraged to inspect the package carefully
                immediately upon delivery.
              </p>

              <p className="mt-3">
                If the outer package appears visibly damaged, crushed, torn,
                punctured or otherwise compromised, please photograph the
                package{" "}
                <strong className="text-black">before opening it</strong> and
                contact Legacy Decor as soon as possible.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                8. Claims Without Sufficient Evidence
              </h2>

              <p>
                Because our products are fragile and may be susceptible to
                damage after delivery, Legacy Decor reserves the right to
                request reasonable photographic/video evidence before approving
                a transportation-damage claim.
              </p>

              <p className="mt-3">
                A claim may be rejected where sufficient evidence is not
                provided to establish that the damage occurred during
                transportation.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                9. Cancellation of Orders
              </h2>

              <p>
                Once an order has been dispatched, cancellation may not be
                possible.
              </p>

              <p className="mt-3">
                If you wish to cancel an order, please contact us immediately
                after placing the order. Cancellation requests will be
                considered only if the order has not yet been processed or
                dispatched.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                10. Customer Support
              </h2>

              <p>
                For reporting transportation damage or raising any concern
                regarding your order, please contact us at:
              </p>

              <div className="mt-4 space-y-2">
                <p>
                  <strong className="text-black">Legacy Decor</strong>
                </p>

                <p>
                  Website:{" "}
                  <a
                    href="https://www.legacydecor.in/"
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    www.legacydecor.in
                  </a>
                </p>

                <p>
                  Email:{" "}
                  <a
                    href="mailto:legacydecorvintage@gmail.com"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    legacydecorvintage@gmail.com
                  </a>
                </p>

                <p>
                  Phone/WhatsApp:{" "}
                  <a
                    href="tel:+919825081632"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    (+91) 9825081632
                  </a>
                </p>
              </div>

              <p className="mt-4">
                Please mention your{" "}
                <strong className="text-black">Order Number</strong> in all
                communications.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                11. Important Note
              </h2>

              <p>
                Nothing in this policy is intended to limit or exclude any
                rights or remedies available to customers under applicable
                Indian laws.
              </p>

              <p className="mt-3">
                Legacy Decor reserves the right to review each damage claim on
                its individual merits and to take appropriate action in
                accordance with applicable law.
              </p>
            </section>
          </div>

          {/* Quick Summary */}
          <div className="mt-12 p-6 sm:p-8 bg-gray-50 border border-gray-200 rounded-xl">
            <h2 className="text-xl font-semibold text-black mb-5">
              Quick Summary
            </h2>

            <p className="font-semibold text-black leading-7">
              No general returns. No change-of-mind refunds. No exchanges for
              personal preference.
            </p>

            <p className="mt-4 text-gray-700 leading-7">
              Refund/replacement is available only for a product that is
              established to have been damaged during transportation, subject to
              timely reporting and supporting evidence, and subject always to
              applicable law.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
